/**
 * Optional, tenant-toggleable modules. Core areas (overview, accounting,
 * inventory, sales, purchasing, reports, settings) are always on; only these
 * self-contained add-on sections can be turned off per tenant.
 */
export const TOGGLEABLE_MODULES = ['files', 'crm', 'hr', 'installments', 'expenses', 'vouchers', 'pos', 'coupons', 'discounts', 'cashback', 'mandob', 'quotations', 'cms', 'audit', 'pricing', 'ai', 'imagegen', 'customer_display', 'returns', 'fib', 'otp', 'reviews', 'wishlist', 'stocktake', 'treasury', 'batches', 'variants', 'storefront_ai', 'delivery', 'storefront_whatsapp', 'tax', 'stories', 'manual_sales', 'manager_products', 'multi_category', 'manufacturing', 'combos', 'ai_reports', 'custom_fields', 'tax_iq', 'tax_de'] as const;
export type ModuleKey = (typeof TOGGLEABLE_MODULES)[number];

/** nav.section.* labelKey → module key (only the toggleable sections). */
export const SECTION_TO_MODULE: Record<string, ModuleKey> = {
  'nav.section.files': 'files',
  'nav.section.crm': 'crm',
  'nav.section.hr': 'hr',
  'nav.section.installments': 'installments',
  'nav.section.expenses': 'expenses',
  'nav.section.pos': 'pos',
  'nav.section.coupons': 'coupons',
  'nav.section.cashback': 'cashback',
  'nav.section.mandob': 'mandob',
  // quotations is NOT here any more: the item moved into the always-on Sales section
  // and carries its own item-level `module` gate instead.
  'nav.section.cms': 'cms',
  // imagegen is NOT here any more: the studio moved into the always-on Settings
  // section and carries its own item-level `module` gate instead (same story as
  // quotations above).
  'nav.section.delivery': 'delivery',
  'nav.section.marketing': 'stories',
  'nav.section.manufacturing': 'manufacturing',
};

/**
 * Permission domain (the bit before the first dot) → the module that owns it.
 *
 * Only listed where a toggleable module owns the whole domain. Most domains are
 * core and never appear here — and several modules deliberately do not, because
 * they ride on somebody else's permissions rather than having their own:
 * expenses, treasury and vouchers are all `gl.*`; stocktake, batches, variants,
 * combos, tax and multi_category are `stock.*` / `items.*`. Gating those domains
 * on a module would hide permissions that other, always-on parts of the app need.
 */
export const PERMISSION_DOMAIN_TO_MODULE: Record<string, ModuleKey> = {
  ai: 'ai',
  ai_reports: 'ai_reports',
  attachments: 'files',
  audit_logs: 'audit',
  cashback: 'cashback',
  cms: 'cms',
  coupons: 'coupons',
  crm: 'crm',
  delivery: 'delivery',
  discounts: 'discounts',
  hr: 'hr',
  imagegen: 'imagegen',
  installments: 'installments',
  mandob: 'mandob',
  manufacturing: 'manufacturing',
  pos: 'pos',
  quotations: 'quotations',
  stories: 'stories',
};

/** The module a permission code belongs to, or null when it is core. */
export function moduleForPermission(code: string): ModuleKey | null {
  return PERMISSION_DOMAIN_TO_MODULE[code.split('.')[0] ?? ''] ?? null;
}

/**
 * A module counts as enabled unless the tenant explicitly set it to `false`.
 * A null/missing map (not loaded yet, or never configured) ⇒ enabled, so
 * nothing is ever wrongly hidden and existing tenants are unaffected.
 */
export function moduleEnabled(
  enabled: Record<string, boolean> | null | undefined,
  key: ModuleKey,
): boolean {
  if (!enabled) return true;
  return enabled[key] !== false;
}
